import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  Calendar,
  Users,
  BarChart3,
  FileText,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../components/AuthProvider';
import { supabase } from '../lib/auth';
import { getClientById } from '../lib/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Button from '../components/ui/Button';
import ClientInfoCard from '../components/clientdetail/ClientInfoCard';
import ClientToolsCard from '../components/clientdetail/ClientToolsCard';
import ContractsTab from '../components/clientdetail/ContractsTab';
import AppointmentsTab from '../components/clientdetail/AppointmentsTab';
import StatisticsTab from '../components/clientdetail/StatisticsTab';
import ProspectingTab from '../components/clientdetail/ProspectingTab';

type Tab = 'infos' | 'contracts' | 'appointments' | 'performance' | 'prospecting';

interface ClientData {
  id: string;
  name: string;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  statut: string;
  created_at: string;
  days_per_month: number | null;
  monthly_price: number | null;
  start_date: string | null;
  end_date: string | null;
  drive_url: string | null;
}

interface ClientStats {
  totalRdv: number;
}

const ClientDetailNewPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('infos');
  const [client, setClient] = useState<ClientData | null>(null);
  const [stats, setStats] = useState<ClientStats>({ totalRdv: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tabs = [
    { id: 'infos' as Tab, label: 'Infos', icon: FileText },
    { id: 'contracts' as Tab, label: 'Contrats', icon: FileText },
    { id: 'appointments' as Tab, label: 'Rendez-vous', icon: Calendar },
    { id: 'performance' as Tab, label: 'Performance', icon: BarChart3 },
    { id: 'prospecting' as Tab, label: 'Prospection', icon: Users }
  ];

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  const fetchClientData = async () => {
    if (!clientId) return;

    try {
      setIsLoading(true);
      setError(null);

      const data = await getClientById(clientId);
      setClient(data);

      const { data: appointmentsData } = await supabase
        .from('appointments')
        .select('id')
        .eq('client_id', clientId);

      setStats({
        totalRdv: appointmentsData?.length || 0
      });
    } catch (err: any) {
      console.error('Error fetching client:', err);
      setError(err.message || 'Erreur lors du chargement du client');
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (name: string) => {
    if (!name) return '?';

    const lettersOnly = name.replace(/[^A-Za-z\s]/g, '');
    if (lettersOnly.length === 0) return '?';

    const words = lettersOnly.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }

    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatusBadge = (statut: string) => {
    switch (statut.toLowerCase()) {
      case 'actif':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'signed':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'cloture':
      case 'clôturé':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusLabel = (statut: string) => {
    switch (statut.toLowerCase()) {
      case 'actif':
        return 'Actif';
      case 'signed':
        return 'Signé';
      case 'cloture':
      case 'clôturé':
        return 'Clôturé';
      default:
        return statut;
    }
  };

  const handleUpdateField = async (field: string, value: string) => {
    if (!clientId) return;

    try {
      const { error } = await supabase
        .from('clients')
        .update({ [field]: value })
        .eq('id', clientId);

      if (error) throw error;

      setClient((prev) => prev ? { ...prev, [field]: value } : null);
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-rose-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" text="Chargement du client..." />
          </div>
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-rose-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-100 to-rose-100 rounded-full flex items-center justify-center">
              <AlertCircle className="h-12 w-12 text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{error || 'Client non trouvé'}</h3>
            <Button onClick={() => navigate('/clients')} className="mt-4">
              Retour aux clients
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Link
            to="/clients"
            className="inline-flex items-center text-gray-600 hover:text-rose-600 transition-colors group"
          >
            <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Retour aux clients</span>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6"
        >
          <div className="px-8 py-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-[#E91E8C] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-white">
                    {getInitials(client.name)}
                  </span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-1">{client.name}</h1>
                  {client.company_name && (
                    <p className="text-gray-500">{client.company_name}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold border ${getStatusBadge(client.statut)}`}>
                  {getStatusLabel(client.statut)}
                </span>
                <button
                  onClick={fetchClientData}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Actualiser
                </button>
                <button
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Modifier
                </button>
              </div>
            </div>

            <div className="flex items-center gap-6 text-sm text-gray-600 mb-6">
              {client.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <span>{client.email}</span>
                </div>
              )}
              {client.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span>{client.phone}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {client.start_date && client.end_date ? (
                  <span>Du {formatDate(client.start_date)} au {formatDate(client.end_date)}</span>
                ) : (
                  <span className="text-orange-600 font-medium">Contrat manquant</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 pt-6 border-t border-gray-200">
              <div>
                <div className="text-sm text-gray-500 mb-1">Total RDV</div>
                <div className="text-2xl font-bold text-gray-900">{stats.totalRdv}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Revenu mensuel</div>
                <div className="text-2xl font-bold text-gray-900">
                  {client.monthly_price ? `${client.monthly_price.toLocaleString('fr-FR')}€` : '-'}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 bg-white">
            <div className="px-8">
              <nav className="flex space-x-1 -mb-px overflow-x-auto">
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  const Icon = tab.icon;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`relative flex items-center space-x-2 px-6 py-4 text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                        isActive
                          ? 'text-[#E91E8C] border-b-2 border-[#E91E8C]'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className={activeTab === 'infos' || activeTab === 'contracts' || activeTab === 'appointments' ? '' : 'bg-white rounded-2xl shadow-lg border border-gray-200 p-8'}
          >
            {activeTab === 'infos' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ClientInfoCard client={client} onUpdate={handleUpdateField} />
                <ClientToolsCard client={client} onUpdate={handleUpdateField} />
              </div>
            )}

            {activeTab === 'contracts' && <ContractsTab clientId={clientId!} />}

            {activeTab === 'appointments' && <AppointmentsTab clientId={clientId!} />}

            {activeTab === 'performance' && <StatisticsTab clientId={clientId!} />}

            {activeTab === 'prospecting' && <ProspectingTab clientId={clientId!} />}
          </motion.div>
        </AnimatePresence>
      </div>

    </div>
  );
};

export default ClientDetailNewPage;
